/**
 * Backend enum-label maps.
 *
 * The live API transports enum-typed fields (`SalesOrder.status`,
 * `Invoice.gstType`, `Payment.mode`, …) as **`int32`**, and the swagger
 * doesn't list the names — so these maps are the frontend's only source
 * of truth for turning those ints into text.
 *
 * ─── Provenance (audited 2026-08-02) ────────────────────────────────
 * There are three competing definitions in this codebase. They do NOT
 * agree, so it matters which one each map follows:
 *
 *   1. `Kristy.Mart.Core/Enums/Enums.cs` — DEAD CODE. Referenced only by
 *      two doc comments; no column is typed to it and no code branches on
 *      it. Every DB column is a plain `int`. Treat as aspirational only.
 *   2. Service/repository constants (e.g. `MovementType`,
 *      `PurchaseOrderService.StatusDelivered`) — LIVE. Real branching.
 *   3. This file + the panel's form pages — LIVE. What actually gets
 *      written to the database by the admin UI.
 *
 * Where (2) and (3) disagreed, the live behaviour won and the losing side
 * was corrected in the same change. Where only (1) disagreed, it was
 * ignored — matching stored data matters more than matching dead code.
 *
 * Each map below records which source it follows. Do not "tidy" these
 * against `Enums.cs` without a data migration: the integers already sitting
 * in the tenant's tables were written under these meanings.
 *
 * Usage:
 * ```ts
 * import { enumLabel, enumEntries } from '@kss/api';
 * <StatusBadge status={enumLabel('SalesOrderStatus', order.status).toLowerCase()} />
 * // dropdowns — never hand-roll a private label→int map in a form:
 * options={enumEntries('SalesOrderStatus')}
 * ```
 *
 * Unknown values fall back to `Status #N` so a UI surface never hard-crashes
 * if the backend introduces a new enum value the frontend hasn't seen yet.
 */

/* Source: panel NewSalesOrder.tsx + OrderReturnService.SalesOrderStatus_Returned = 6.
   `Enums.cs OrderStatus` disagrees (it inserts Processing at 3 and runs to 8)
   but nothing reads it. */
export const SalesOrderStatusLabels = {
  1: 'Draft',
  2: 'Confirmed',
  3: 'Shipped',
  4: 'Delivered',
  5: 'Cancelled',
  /**
   * Code-owned extension (no DB schema change; the column remains `int`).
   * Backend `OrderReturnService.AddAsync` patches the linked SalesOrder to
   * this value when a return is created, so the storefront's
   * `/account/orders` page can route it under "Returns & Refunds" and the
   * panel grid can render a "Returned" badge.
   */
  6: 'Returned',
} as const;
export type SalesOrderStatus = number;

/* Source: panel NewSalesOrder.tsx CHANNEL_BY_LABEL, and
   PublicCheckoutController.cs (`Channel = 3, // 3 = Web storefront`).
   Previously listed 3='B2B', 4='Phone', which mislabelled every web order. */
export const SalesOrderChannelLabels = {
  1: 'POS',
  2: 'Online',
  3: 'Web',
  4: 'B2B',
  5: 'B2C',
  6: 'Marketplace',
} as const;
export type SalesOrderChannel = number;

/* Source: panel NewSalesOrder.tsx PAYMENT_STATUS_BY_LABEL (Enums.cs agrees).
   Previously 1='Unpaid' with no 5, so Failed payments rendered as a raw int. */
export const PaymentStatusLabels = {
  1: 'Pending',
  2: 'Partial',
  3: 'Paid',
  4: 'Refunded',
  5: 'Failed',
} as const;
export type PaymentStatus = number;

/* Source: panel NewInvoice.tsx STATUS_BY_LABEL.
   Previously 4='PartiallyPaid', 5='Cancelled', 6='Overdue' — which meant an
   invoice saved as Overdue (4) displayed as "PartiallyPaid", and the backend's
   cancelled-invoice filters (fixed alongside this to 5) excluded it from
   dashboard revenue and AR aging. */
export const InvoiceStatusLabels = {
  1: 'Draft',
  2: 'Issued',
  3: 'Paid',
  4: 'Overdue',
  5: 'Cancelled',
} as const;
export type InvoiceStatus = number;

/* Source: panel NewInvoice.tsx GST_TYPE_BY_LABEL (Enums.cs agrees:
   CGST_SGST / IGST / Exempt). Previously claimed 5 values including
   'Composition', 'NonGst' and 'Exempted', none of which the backend or the
   form can produce — an Exempt invoice (3) displayed as "Composition". */
export const GstTypeLabels = {
  1: 'Intra-state (CGST+SGST)',
  2: 'Inter-state (IGST)',
  3: 'Exempt',
} as const;
export type GstType = number;

/* Source: panel NewPayment.tsx / NewExpense.tsx MODE_BY_LABEL.
   Previously 7='COD'; the forms have always written 7 for 'Other'.
   NOTE: the storefront maps its COD option to 1 (Cash) — see
   CheckoutPage.tsx PAYMENT_MODE_INT. */
export const PaymentModeLabels = {
  1: 'Cash',
  2: 'Card',
  3: 'UPI',
  4: 'Net banking',
  5: 'Cheque',
  6: 'Wallet',
  7: 'Other',
} as const;
export type PaymentMode = number;

/* Source: panel NewAttendance.tsx STATUS_BY_LABEL — i.e. the values already
   sitting in the Attendance table.
   Previously 4='HalfDay', 5='Holiday', which inverted every stored row.
   `Enums.cs AttendanceStatus` orders these differently again (3=HalfDay,
   4=Leave); it is dead code and following it would silently rewrite the
   meaning of historical, payroll-relevant attendance records. 6 is kept
   available for display so a future WeekOff value never renders raw. */
export const AttendanceStatusLabels = {
  1: 'Present',
  2: 'Absent',
  3: 'Leave',
  4: 'Holiday',
  5: 'Half-day',
  6: 'Week off',
} as const;
export type AttendanceStatus = number;

/* Source: panel NewPayroll.tsx STATUS_BY_LABEL (Enums.cs agrees).
   Previously 2='Approved' — a payroll saved as Processed read back as Approved. */
export const PayrollStatusLabels = {
  1: 'Draft',
  2: 'Processed',
  3: 'Paid',
  4: 'Cancelled',
} as const;
export type PayrollStatus = number;

export const ExpensePaymentModeLabels = PaymentModeLabels;

export const OrderReturnTypeLabels = {
  1: 'SalesOrder',
  2: 'PurchaseOrder',
} as const;
export type OrderReturnType = number;

/* Source: panel NewAccount.tsx TYPE_BY_LABEL.
   Previously advertised 6='Bank' and 7='CashInHand'. Neither exists on the
   backend, so those options could never round-trip; the audit's claim that
   "Bank accounts cannot be created" was really "those values are fictional".
   Ledger-side bank accounts are modelled as Asset (1). */
export const AccountTypeLabels = {
  1: 'Asset',
  2: 'Liability',
  3: 'Equity',
  4: 'Revenue',
  5: 'Expense',
} as const;
export type AccountType = number;

export const JournalEntryStatusLabels = {
  1: 'Draft',
  2: 'Posted',
  3: 'Reversed',
} as const;
export type JournalEntryStatus = number;

/* Source: LIVE backend constant `Kristy.Mart.Library/Common/Inventory/MovementType.cs`
   — the authority here, because StockReservationService / SalesOrderService /
   InvoiceService / PurchaseOrderService all write these values.
   Previously 4='Adjustment', 5='Return', so every soft-hold reservation row
   the backend emitted displayed in the panel as an "Adjustment" and every
   release as a "Return". Manual stock corrections belong to the separate
   StockAdjustment module, not to this enum. */
export const StockMovementTypeLabels = {
  1: 'In',
  2: 'Out',
  3: 'Transfer',
  4: 'Reserve',
  5: 'Release',
} as const;
export type StockMovementType = number;

export const UserStatusLabels = {
  1: 'Active',
  2: 'Inactive',
  3: 'Locked',
} as const;
export type UserStatus = number;

/* No panel form wrote valid ints here — NewLicense.tsx sent label STRINGS into
   an int column, so AllLicenses rendered `License Status #NaN`. The form is now
   wired to these values via enumEntries('LicenseStatus'). */
export const LicenseStatusLabels = {
  1: 'Active',
  2: 'Trial',
  3: 'Expired',
  4: 'Suspended',
  5: 'Cancelled',
} as const;
export type LicenseStatus = number;

export const TenantStatusLabels = {
  1: 'Active',
  2: 'Suspended',
  3: 'Deactivated',
} as const;
export type TenantStatus = number;

export const TenantDomainStatusLabels = {
  1: 'Active',
  2: 'Inactive',
} as const;
export type TenantDomainStatus = number;

/* Aligned to the same Draft/Confirmed/Shipped/Delivered/Cancelled chain as
   SalesOrder. LOAD-BEARING: `PurchaseOrderService.StatusDelivered = 4` keys
   the Stock UPSERT + StockMovement.In receipt trigger off Delivered = 4, and
   documents that it follows this map rather than `Enums.cs`. Changing these
   numbers changes when stock is received. */
export const PurchaseOrderStatusLabels = {
  1: 'Draft',
  2: 'Confirmed',
  3: 'Shipped',
  4: 'Delivered',
  5: 'Cancelled',
} as const;
export type PurchaseOrderStatus = number;

/* Source: panel NewEmployee.tsx STATUS_OPTIONS (Enums.cs agrees).
   Previously 3='Terminated', 4='Resigned' — inverted, so terminating an
   employee displayed them as resigned and vice versa. */
export const EmployeeStatusLabels = {
  1: 'Active',
  2: 'On leave',
  3: 'Resigned',
  4: 'Terminated',
} as const;
export type EmployeeStatus = number;

export const SubscriptionPlanTierLabels = {
  0: 'Free',
  1: 'Pro',
  2: 'Business',
  3: 'Enterprise',
} as const;
export type SubscriptionPlanTier = number;

export const PayslipStatusLabels = PayrollStatusLabels;

/* ---------------------------------------------------------------------- */
/* Lookup helper                                                            */
/* ---------------------------------------------------------------------- */

export type EnumKind =
  | 'SalesOrderStatus' | 'SalesOrderChannel' | 'PaymentStatus' | 'InvoiceStatus'
  | 'GstType' | 'PaymentMode' | 'AttendanceStatus' | 'PayrollStatus'
  | 'ExpensePaymentMode' | 'OrderReturnType' | 'AccountType' | 'JournalEntryStatus'
  | 'StockMovementType' | 'UserStatus' | 'LicenseStatus' | 'TenantStatus'
  | 'TenantDomainStatus' | 'PurchaseOrderStatus' | 'EmployeeStatus'
  | 'SubscriptionPlanTier' | 'PayslipStatus';

const REGISTRY: Record<EnumKind, Readonly<Record<number, string>>> = {
  SalesOrderStatus: SalesOrderStatusLabels,
  SalesOrderChannel: SalesOrderChannelLabels,
  PaymentStatus: PaymentStatusLabels,
  InvoiceStatus: InvoiceStatusLabels,
  GstType: GstTypeLabels,
  PaymentMode: PaymentModeLabels,
  AttendanceStatus: AttendanceStatusLabels,
  PayrollStatus: PayrollStatusLabels,
  ExpensePaymentMode: ExpensePaymentModeLabels,
  OrderReturnType: OrderReturnTypeLabels,
  AccountType: AccountTypeLabels,
  JournalEntryStatus: JournalEntryStatusLabels,
  StockMovementType: StockMovementTypeLabels,
  UserStatus: UserStatusLabels,
  LicenseStatus: LicenseStatusLabels,
  TenantStatus: TenantStatusLabels,
  TenantDomainStatus: TenantDomainStatusLabels,
  PurchaseOrderStatus: PurchaseOrderStatusLabels,
  EmployeeStatus: EmployeeStatusLabels,
  SubscriptionPlanTier: SubscriptionPlanTierLabels,
  PayslipStatus: PayslipStatusLabels,
};

/** Resolve an enum integer to its human label, with `Status #N` fallback. */
export function enumLabel(kind: EnumKind, value: number | null | undefined): string {
  if (value == null) return '—';
  const labels = REGISTRY[kind];
  return (labels as Record<number, string>)[value] ?? `${kind.replace(/([A-Z])/g, ' $1').trim()} #${value}`;
}

/** Inverse — turn a label into an int (case-insensitive). Returns `undefined` when not matched. */
export function enumValue(kind: EnumKind, label: string | null | undefined): number | undefined {
  if (!label) return undefined;
  const target = String(label).toLowerCase();
  const labels = REGISTRY[kind];
  for (const [k, v] of Object.entries(labels)) {
    if (v.toLowerCase() === target) return Number(k);
  }
  return undefined;
}

/**
 * Iterate every (value, label) pair for select-dropdown options.
 *
 * Prefer this over a locally-declared label→int map in a form page: the
 * duplicated maps are what drifted out of sync with the grids in the first
 * place, so that a record saved under one label read back as another.
 */
export function enumEntries(kind: EnumKind): { value: number; label: string }[] {
  return Object.entries(REGISTRY[kind]).map(([v, l]) => ({ value: Number(v), label: l }));
}
